// PURE schema — no framework imports beyond zod, no Prisma, no NestJS.
//
// The single home for "may the server be told to call this URL", for a URL that
// arrives from a tenant and is later fetched SERVER-SIDE with the organization's
// own credential attached.
//
// ## Why this is one exported schema and not a pattern to copy
//
// It used to be a module-local `const` inside
// `api/domains/custom-models/schemas/custom-model.schemas.ts`. Being local, it
// could not be reused, so the Public API v1 write for the SAME Prisma table
// (`CustomModel`) spelled its `baseUrl` as a bare `z.string().url()` instead.
// Two write boundaries onto one table then disagreed, and the weaker one was the
// one a customer API key reached: `http://` endpoints and `169.254.169.254` were
// accepted there and refused internally (NEX-3898).
//
// The stored URL is what inference later calls, with the organization's
// DECRYPTED API key on the request. So an `http://` endpoint ships that key in
// plaintext on every completion, and a private address turns the write into a
// server-side request forgery primitive.
//
// 🚨 A second copy of this refinement is the defect, not the fix. Import this
// schema; never re-declare the two `.refine` calls at a new boundary.

import { z } from "zod";

import { isInternalHostname, normalizeHostname } from "./internal-hostname";
import { isPrivateIpv4Uint32, isPrivateIpv6 } from "./private-ip";

/**
 * Parse dotted-decimal IPv4 into a 32-bit unsigned integer. Returns null when
 * the string is not an IPv4 literal at all.
 *
 * Strict on purpose: each octet must round-trip through `String(Number(part))`,
 * so `010` and `0x7f` are rejected as literals rather than silently reinterpreted.
 * The WHATWG URL parser has already canonicalised those spellings by the time
 * this runs, so a value reaching here in a non-canonical form is not an address.
 */
function parseIPv4(s: string): number | null {
  const parts = s.split(".");
  if (parts.length !== 4) return null;
  let result = 0;
  for (const part of parts) {
    const n = Number(part);
    if (!Number.isInteger(n) || n < 0 || n > 255 || part !== String(n)) return null;
    result = (result * 256 + n) >>> 0;
  }
  return result;
}

/**
 * Returns true if the hostname targets a private/internal address.
 *
 * The range table is `shared/network/private-ip` — the same one the webhook
 * SSRF guard and the backend's client-IP resolver read. A third independent copy
 * of it used to live beside this function, and it was the only one missing
 * `100.64.0.0/10` and the only one that classified the IPv6 first hextet
 * correctly (NEX-3548). `isPrivateIpv6` there also unwraps IPv4-mapped and 6to4
 * addresses, so no separate extraction step is needed here.
 */
function isPrivateHost(hostname: string): boolean {
  // NORMALISE ONCE, BEFORE BOTH CHECKS. The NAME half is the live fix: this used
  // to compare `hostname.toLowerCase()` against an exact set, so `localhost.` —
  // a legal absolute-FQDN spelling that resolves identically — was not the string
  // `localhost` and walked straight past (NEX-3943).
  //
  // ⚠️ The IP half is DEFENSIVE, not a fix, and the difference is measured. The
  // only caller passes `new URL(url).hostname`, and the WHATWG parser has already
  // canonicalised every IP literal by then — it drops a trailing dot from
  // `169.254.169.254.` and rewrites `2130706433`, `0x7f000001` and `127.1` to
  // dotted-quad. So no trailing dot can reach `parseIPv4` through this path, and
  // a mutation handing it the RAW hostname passes all 112 conformance cases.
  // It is kept because `isPrivateHost` should not depend on its caller having
  // gone through a URL parser, and it is labelled because an unreachable branch
  // that reads as load-bearing is how the next reader mis-sizes the guard.
  //
  // `isInternalHostname` deliberately does NOT normalise again — see its
  // docblock; normalising twice hid a one-dot-strip defect from its own test.
  const host = normalizeHostname(hostname);

  if (isInternalHostname(host)) return true;

  // Strip IPv6 brackets: "[::1]" -> "::1"
  const bare = host.startsWith("[") && host.endsWith("]") ? host.slice(1, -1) : host;

  // `parseIPv4` is the strict "is this an IPv4 literal at all" test; the range
  // decision itself is the shared table's.
  const v4 = parseIPv4(bare);
  if (v4 !== null) return isPrivateIpv4Uint32(v4);

  if (bare.includes(":")) return isPrivateIpv6(bare);

  return false;
}

/**
 * Validates a URL is safe for external API calls:
 * - Must be HTTPS
 * - Must not target private/internal networks (SSRF prevention)
 *
 * Uses numeric IP parsing instead of regex — handles all representations
 * including IPv4-mapped IPv6 (::ffff:10.0.0.1), hex forms, etc.
 *
 * This is the WRITE-boundary guard. Response schemas that merely echo a stored
 * `baseUrl` deliberately stay `z.string().url()`: tightening a read would make a
 * legacy row unreadable rather than unwritable, which turns a hardening change
 * into an outage on the list endpoint.
 */
export const safeExternalUrlSchema = z
  .string()
  .trim()
  .url()
  .refine((url) => url.startsWith("https://"), {
    message: "Only HTTPS URLs are allowed"
  })
  .refine(
    (url) => {
      try {
        return !isPrivateHost(new URL(url).hostname);
      } catch {
        return false;
      }
    },
    { message: "URLs targeting private or internal networks are not allowed" }
  );

/**
 * The same guard, for a write boundary where the EMPTY STRING is the console's
 * spelling of "no URL".
 *
 * 🔴 **THIS IS NOT A CONVENIENCE. Without it, tightening these fields is an
 * outage.** `AgentExternalTool.endpointUrl` is nullable and 2747 of 4183 rows on
 * production hold NULL. The console's edit form reads `tool.endpointUrl || ""`
 * into local state and posts that string back on every save, so a strict
 * `.url()` on those bodies would answer 400 to any edit of a tool that has no
 * endpoint — which is most of them — while the URL being edited was never the
 * field the user touched.
 *
 * The empty string is therefore ACCEPTED and means absent. Any non-empty value
 * is judged by {@link safeExternalUrlSchema}, unchanged. This composes the one
 * guard rather than re-declaring its refinements, which is what the header above
 * forbids.
 *
 * ⚠️ **A `z.union([z.literal(""), safeExternalUrlSchema])` accepts and refuses
 * exactly the same values, and is WORSE at the boundary.** `packages/cli`'s `gen:contract-help`
 * walks the contract to type each flag, cannot classify a union, and emits
 * `type: "unknown"` — so `nexus skills update-external-tool --endpoint-url`
 * loses its type in the operator's own help output. A refined `z.string()`
 * stays a string to that walker and delegates to the one guard just the same.
 */
export const optionalSafeExternalUrlSchema = z.string().transform((value, ctx) => {
  if (value === "") return "";
  const result = safeExternalUrlSchema.safeParse(value);
  if (!result.success) {
    for (const issue of result.error.issues) {
      ctx.addIssue({ code: "custom", message: issue.message });
    }
    return z.NEVER;
  }
  // 🔴 RETURN THE PARSED VALUE, NEVER `value`. `safeExternalUrlSchema` begins
  // `.string().trim()`, so its OUTPUT is the trimmed URL. An earlier version of
  // this ran the nested parse only to decide accept/refuse and then handed back
  // the original string, which silently dropped the trim: `" https://x "` was
  // judged valid and STORED with its padding. That is a transform quietly lost
  // by a refactor, and the comment claiming runtime equivalence with the old
  // union was what stopped it being noticed. (bugbot, #4031.)
  return result.data;
});
